import {AppointmentType} from './enums';
import {
  AppointmentDetail,
  AppointmentInfo,
  BookingDetails,
  CalendarEvent,
} from './type';
import moment from 'moment';
import {Color} from '../../constants/style';

export function appointmentInfoToCalendarEvent(
  info: AppointmentInfo
): CalendarEvent {
  const {appointment} = info;

  return {
    appointmentId: appointment.id,
    patientId: appointment.patientId,
    patientName: appointment.patientName,
    status: appointment.status,
    title: appointment.type,
    isConfirmed: appointment.status === AppointmentType.Confirmed,
    start: appointment.startTime,
    end: appointment.endTime,
  };
}

export const extractAppointmentDetails = (details: BookingDetails) => {
  const {
    id,
    status,
    appointment_type,
    care_type,
    sub_care_type,
    preferred_time,
    doctor,
    date,
    check_in_time,
    check_out_time,
    confirm_at,
    start_time,
  } = details.appointment;

  return {
    appointmentId: id,
    appointment_type_title: appointment_type.title,
    care_type_title: care_type.title,
    sub_care_type_title: sub_care_type.title,
    preferred_time: preferred_time,
    doctor_full_name: doctor?.full_name || 'No preference',
    date: date,
    status: status,
    check_in_time,
    check_out_time,
    confirm_at,
    start_time,
  };
};

export const extractFinancialDetails = (details: BookingDetails) => {
  const {
    id: appointment_id,
    payment,
    self_paying,
    insurance_claim,
    status: appointment_status,
  } = details.appointment;

  let payment_status = '';
  if (self_paying) {
    payment_status = payment?.status || '';
  }

  return {
    appointment_id,
    self_paying,
    insurance_claim: {
      insurance_id: insurance_claim?.insurance_id?.toString() || '',
      insurer_name: insurance_claim?.insurer_name || '',
      membership_no: insurance_claim?.membership_no || '',
      pre_authorisation_code:
        insurance_claim?.pre_authorisation_code?.toString() || undefined,
    },
    payment: payment
      ? {
          id: payment.id,
          payment_method: payment.payment_method,
        }
      : undefined,
    payment_status,
    appointment_status,
  };
};

export const extractContactDetails = (details: BookingDetails) => {
  const {contact_email, contact_mobile_number, preferred_point_of_contact} =
    details.appointment;

  return {
    email: contact_email,
    mobile_number: contact_mobile_number,
    preferred_point_of_contact,
  };
};

export const extractPatientDetails = (details: BookingDetails) => {
  const {gender, dob, full_name} = details.appointment.patient;
  const {self_booking, referred_by_physician, reason} = details.appointment;
  const {line1, line2, postcode, city, city_name, country, country_name} =
    details.appointment.patient.address;

  return {
    gender,
    dob,
    self_booking: self_booking ? 'Yes' : 'No',
    referred_by_physician: referred_by_physician ? 'Yes' : 'No',
    line1,
    line2: line2 || '',
    postcode,
    city,
    country,
    city_name,
    country_name,
    reason,
    full_name,
  };
};
export const formatDateUK = (date: Date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const day = days[date.getDay()];
  const dateNumber = String(date.getDate()).padStart(2, '0');

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const time = `${hours}:${minutes}`;

  return {
    day,
    date: dateNumber,
    time,
  };
};

export const formatAppointmentDate = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
  };

  return date.toLocaleDateString(undefined, options);
};

export function appointmentDetailToCalendarEvent(
  details: AppointmentDetail,
  override?: Partial<CalendarEvent>
): CalendarEvent {
  const {appointment, appointmentType, patient} = details;

  return {
    appointmentId: appointment.id,
    patientId: patient.id,
    patientName: patient.firstName + ' ' + patient.lastName,
    title: appointmentType.title,
    status: appointment.status,
    isConfirmed: appointment.status === AppointmentType.Confirmed,
    start: appointment.startTime!,
    end: appointment.endTime!,
    ...override,
  };
}

export function timeAgo(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const intervals: {[key: string]: number} = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hr: 3600,
    min: 60,
    sec: 1,
  };

  for (const interval in intervals) {
    const value = Math.floor(seconds / intervals[interval]);
    if (value > 0) {
      return value === 1 ? `${value} ${interval}` : `${value} ${interval}s`;
    }
  }
  return 'just now';
}

export const parseSnakeCase = (status: string) => {
  const tenseMap: {[key: string]: string} = {
    confirm: 'Confirmed',
    cancel: 'Cancelled',
    to_be_confirm: 'To be confirmed',
    complete: 'Complete',
  };
  return tenseMap[status] || status;
};

export const getChipStatusColors = (status: string) => {
  switch (status) {
    case 'confirm':
      return {bgColor: '#03820F', textColor: '#ffffff'};
    case 'complete':
      return {bgColor: '#03820F', textColor: '#ffffff'};
    case 'cancel':
      return {bgColor: '#D92D20', textColor: '#ffffff'};
    case 'to_be_confirm':
      return {bgColor: '#FEF0C7', textColor: '#1D2939'};
    default:
      return {bgColor: '#D92D20', textColor: '#ffffff'};
  }
};

export const truncateText = (text: string, maxLength: number = 20) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const getPaymentStatusDisplay = (status: string): string => {
  const parsedStatus = parseSnakeCase(status.toLowerCase());

  if (parsedStatus === status) {
    switch (status) {
      case 'succeeded':
      case 'paid':
        return 'Paid';
      case 'failed':
      case 'canceled':
      case 'unpaid':
        return 'Unpaid';
      case 'pending':
        return 'Pending';
      case 'refunded':
        return 'Refunded';
      case 'refund_requested':
        return 'Refund Requested';
      default:
        return '----';
    }
  }

  return parsedStatus;
};

export const getInsuranceClaimStatusDisplay = (status: string): string => {
  const parsedStatus = parseSnakeCase(status.toLowerCase());

  if (parsedStatus === status) {
    switch (status) {
      case 'new_claim':
        return 'New Claim';
      case 'submitted':
        return 'Submitted';
      case 'rejected':
        return 'Rejected';
      case 'outstanding':
        return 'Outstanding';
      case 'paid':
        return 'Paid';
      case 'pending':
        return 'Pending';
      default:
        return status.replace('_', ' ');
    }
  }

  return parsedStatus;
};

export type PaymentStatusInput =
  | 'succeeded'
  | 'paid'
  | 'failed'
  | 'canceled'
  | 'unpaid'
  | 'pending'
  | 'refunded'
  | 'refund_requested';
export type PaymentStatusType =
  | 'paid'
  | 'unpaid'
  | 'pending'
  | 'refunded'
  | 'refund_requested';

export const getPaymentStatusType = (
  status: PaymentStatusInput
): PaymentStatusType => {
  const normalizedStatus = status.toLowerCase().trim();

  switch (normalizedStatus) {
    case 'succeeded':
    case 'paid':
      return 'paid';
    case 'failed':
    case 'canceled':
    case 'unpaid':
      return 'unpaid';
    case 'pending':
      return 'pending';
    case 'refunded':
      return 'refunded';
    case 'refund_requested':
      return 'refund_requested';
    default:
      return 'unpaid';
  }
};

export const getPaymentStatusColor = (
  status: 'paid' | 'unpaid' | 'pending' | 'refunded' | 'refund_requested'
): string => {
  switch (status) {
    case 'pending':
      return Color.PaymentPending;
    case 'refunded':
      return Color.PaymentRefunded;
    case 'paid':
      return Color.SuccessMain;
    case 'unpaid':
      return Color.ErrorMain;
    case 'refund_requested':
      return Color.PaymentRefunded;
    default:
      return Color.PaymentRefunded;
  }
};

export const formatCurrency = (amount: number): string => {
  if (isNaN(amount) || amount === 0) return '-';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
};

export const formatDate = (date?: Date | string): string => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

export function getRangeStartDate(
  date: Date,
  mode: 'day' | 'week' | 'month'
): Date {
  const m = moment(date);
  if (mode === 'day') return m.startOf('day').toDate();
  if (mode === 'week') return m.startOf('week').toDate();
  if (mode === 'month') return m.startOf('month').toDate();
  return m.toDate();
}

export function getRangeEndDate(
  date: Date,
  mode: 'day' | 'week' | 'month'
): Date {
  const m = moment(date);
  if (mode === 'day') return m.endOf('day').toDate();
  if (mode === 'week') return m.endOf('week').toDate();
  if (mode === 'month') return m.endOf('month').toDate();
  return m.toDate();
}

export const getStatusChipStyles = (
  status?: 'paid' | 'unpaid' | 'pending' | 'refunded' | 'refund_requested'
) => ({
  backgroundColor: (() => {
    switch (status) {
      case 'paid':
        return Color.SuccessMain;
      case 'pending':
        return Color.PendingMain;
      case 'refunded':
      case 'refund_requested':
        return Color.Grey900;
      default:
        return Color.ErrorMain;
    }
  })(),
  color: (() => {
    switch (status) {
      case 'refunded':
      case 'refund_requested':
        return Color.Black;
      default:
        return Color.White;
    }
  })(),
  height: '32px',
  width: status === 'refund_requested' ? '140px' : '84px',
  borderRadius: '8px',
  overflow: 'visible',
  '& .MuiChip-label': {
    fontSize: '14px',
    padding: '7px 12px',
    fontWeight: '400',
    lineHeight: '18px',
    overflow: 'visible',
  },
});

export function formatMonthYear(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('default', {month: 'long', year: 'numeric'});
}
